import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query";

import { env } from "@/app/utils/env";
import { getToken } from "@/utils/storage/appSession";

type QueryParamValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | (string | number | boolean | null | undefined)[];

const serializeQueryParams = (
  queryParams: Record<string, QueryParamValue> | URLSearchParams,
): string => {
  const searchParams = new URLSearchParams();

  if (queryParams instanceof URLSearchParams) {
    queryParams.forEach((value, key) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, String(value));
      }
    });

    return searchParams.toString();
  }

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((itemValue) => {
        if (itemValue === undefined || itemValue === null || itemValue === "") {
          return;
        }

        searchParams.append(key, String(itemValue));
      });

      return;
    }

    searchParams.append(key, String(value));
  });

  return searchParams.toString();
};

/**
 * Creates the authenticated RTK base query used by all protected API endpoints.
 *
 * @returns RTK base query with token header, backend base URL, timeout, and param serialization.
 */
export const createAuthBaseQuery = (): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> =>
  fetchBaseQuery({
    baseUrl: env.BACKEND_URL,
    timeout: 60000,
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = getToken();

      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
    paramsSerializer: serializeQueryParams,
  });
